import { API_URL } from '../config';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { api, sessionsApi, basesApi, disparosApi, templatesApi, Session, Base, Disparo, Template, Botao, Resposta } from '../api/api';

interface DispatchProgress {
  disparoId?: number;
  total: number;
  sent: number;
  pending: number;
  failed: number;
  currentContact?: string;
  sessionUsed?: number;
}

interface MessageStatusEvent {
  messageId: string;
  phone: string;
  dispatchId: string;
  status: 'delivered' | 'read';
  at: string;
}

// dispatchId -> { delivered: number, read: number }
type DispatchReadStats = Record<string, { delivered: number; read: number }>;
// disparoId -> totalRespostas
type DispatchReplyStats = Record<number, number>;
type ReportStats = {
  read: number;
  delivered: number;
  naoEntregues: number;
  readHistorico: number;
  enviadosHistorico: number;
  falhasHistorico: number;
  totalRespostas: number;
};

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pendente:        { label: 'Pendente',                    color: 'text-red-500 bg-red-50 border-red-200' },
  agendado:        { label: 'Agendado',                    color: 'text-blue-700 bg-blue-50 border-blue-200' },
  em_andamento:    { label: 'Disparando',                  color: 'text-yellow-700 bg-yellow-50 border-[rgba(245,158,11,0.3)]' },
  pausado:         { label: 'Pausado',                     color: 'text-yellow-700 bg-yellow-50 border-[rgba(245,158,11,0.3)]' },
  limite_atingido: { label: 'Limite de disparos atingido', color: 'text-orange-700 bg-orange-50 border-[rgba(249,115,22,0.3)]' },
  concluido:       { label: 'Concluído',                   color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  cancelado:       { label: 'Cancelado',                   color: 'text-red-500 bg-red-50 border-red-200' },
};

function fmt(d: string | null) {
  if (!d) return 'â€"';
  return new Date(d).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

function fmtDate(d: string | null) {
  if (!d) return 'â€"';
  return new Date(d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });
}

function formatarNumeroTeste(digits: string): string {
  if (digits.length === 0) return '';
  // 55 (XX) XXXXX-XXXX
  let r = digits.slice(0, 2); // 55
  const rest = digits.slice(2);
  if (rest.length === 0) return r;
  r += ' (' + rest.slice(0, 2);
  if (rest.length <= 2) return r;
  r += ') ' + rest.slice(2, 7);
  if (rest.length <= 7) return r;
  r += '-' + rest.slice(7, 11);
  return r;
}

export default function DispatchPage() {
  const canUseModelosMensagem = (() => {
    try {
      const stored = localStorage.getItem('velox_user');
      if (!stored) return false;
      const user = JSON.parse(stored);
      return user?.tipo === 'administrador' || Array.isArray(user?.telas) && user.telas.includes('/templates');
    } catch {
      return false;
    }
  })();

  // dados
  const [sessions, setSessions] = useState<Session[]>([]);
  const [bases, setBases] = useState<Base[]>([]);
  const [historico, setHistorico] = useState<Disparo[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);

  // modal de criaÃ§Ã£o
  const [modalOpen, setModalOpen] = useState(false);
  // form
  const [nomeDisparo, setNomeDisparo] = useState('');
  const [baseSelecionada, setBaseSelecionada] = useState<Base | null>(null);
  const [baseSearch, setBaseSearch] = useState('');
  const [baseDropOpen, setBaseDropOpen] = useState(false);
  const [intervaloMin, setIntervaloMin] = useState<number | ''>(1);
  const [intervaloMax, setIntervaloMax] = useState<number | ''>(3);
  const [mensagem, setMensagem] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [botoes, setBotoes] = useState<Botao[]>([]);
  const [novoBotao, setNovoBotao] = useState('');
  const [novoBotaoLink, setNovoBotaoLink] = useState('');
  const [novoBotaoRetorno, setNovoBotaoRetorno] = useState('');
  const [modoBotao, setModoBotao] = useState<'normal' | 'link'>('normal');
  const [limiteDiario, setLimiteDiario] = useState(0);
  const [agendar, setAgendar] = useState(false);
  const [dataAgendamento, setDataAgendamento] = useState('');
  const [numeroTeste, setNumeroTeste] = useState('');
  const [cnpjTeste, setCnpjTeste] = useState('');
  const [categoriaTeste, setCategoriaTeste] = useState('');
  const [testeOpen, setTesteOpen] = useState(false);
  const [nomeListaNova, setNomeListaNova] = useState('');
  const [arquivoListaNova, setArquivoListaNova] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);

  // UI
  const [loading, setLoading] = useState(false);
  const [salvandoMensagem, setSalvandoMensagem] = useState(false);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [usarFoto, setUsarFoto] = useState(false);
  const [usarBotoes, setUsarBotoes] = useState(false);
  const [usarModeloMensagem, setUsarModeloMensagem] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<DispatchProgress | null>(null);
  const [disparoAtivo, setDisparoAtivo] = useState<Disparo | null>(null);
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [reportDisparoId, setReportDisparoId] = useState<number | null>(null);
  const reportDisparo = reportDisparoId != null ? (historico.find(d => d.id === reportDisparoId) ?? null) : null;
  // naoEntreguesStats: disparoId -> count (vem do banco, sempre consistente com o modal)
  const [naoEntreguesStats, setNaoEntreguesStats] = useState<Record<number, number>>({});
  const [reportStats, setReportStats] = useState<Record<number, ReportStats>>({});

  const loadReportStats = useCallback(async (id: number) => {
    try {
      const { data } = await disparosApi.getStats(id);
      const key = `disparo_${id}`;
      setReadStats(prev => ({
        ...prev,
        [key]: {
          delivered: data.delivered,
          read: data.read,
        },
      }));
      setNaoEntreguesStats(prev => ({ ...prev, [id]: data.naoEntregues }));
      setReportStats(prev => ({ ...prev, [id]: data }));
      // Atualiza historico com totais acumulados vindos do banco
      setHistorico(prev => prev.map(d =>
        d.id === id
          ? { ...d, totalEnviadosHistorico: data.enviadosHistorico, totalFalhasHistorico: data.falhasHistorico, totalVisualizacoesHistorico: data.readHistorico, totalRespostas: data.totalRespostas }
          : d
      ));
      // Popula replyStats com o valor do banco (garante que abre com valor atualizado)
      setReplyStats(prev => ({ ...prev, [id]: data.totalRespostas }));
    } catch {}
  }, []);

  const openReport = useCallback(async (id: number) => {
    setReportDisparoId(id);
    await loadReportStats(id);
  }, [loadReportStats]);
  const [confirmReenviar, setConfirmReenviar] = useState<Disparo | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Disparo | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (menuOpenId === null) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpenId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpenId]);

  const handleDelete = useCallback(async (d: Disparo) => {
    try {
      await disparosApi.remove(d.id);
      setHistorico(prev => prev.filter(x => x.id !== d.id));
      showToast('Disparo excluÃ­do com sucesso');
    } catch {}
    setConfirmDelete(null);
  }, []);
  const [respostasDisparo, setRespostasDisparo] = useState<{ disparo: Disparo; lista: Resposta[] } | null>(null);
  const [respostasLoading, setRespostasLoading] = useState(false);
  const [naoEntreguesDisparo, setNaoEntreguesDisparo] = useState<{ disparo: Disparo; lista: { phone: string; sentAt: string; status: string }[] } | null>(null);
  const [naoEntreguesLoading, setNaoEntreguesLoading] = useState(false);
  const [visualizaramDisparo, setVisualizaramDisparo] = useState<{ disparo: Disparo; lista: { phone: string; readAt: string }[]; total: number } | null>(null);
  const [visualizaramLoading, setVisualizaramLoading] = useState(false);
  const [visualizaramLoadingMore, setVisualizaramLoadingMore] = useState(false);

  const openRespostas = useCallback(async (d: Disparo) => {
    setRespostasLoading(true);
    setRespostasDisparo({ disparo: d, lista: [] });
    try {
      const { data } = await disparosApi.getRespostas(d.id);
      setRespostasDisparo({ disparo: d, lista: data });
    } catch {
      setRespostasDisparo({ disparo: d, lista: [] });
    } finally {
      setRespostasLoading(false);
    }
  }, []);

  const openVisualizaram = useCallback(async (d: Disparo) => {
    setVisualizaramLoading(true);
    setVisualizaramDisparo({ disparo: d, lista: [], total: 0 });
    try {
      const { data } = await disparosApi.getVisualizaram(d.id, 0, 15);
      setVisualizaramDisparo({ disparo: d, lista: data.items, total: data.total });
    } catch {
      setVisualizaramDisparo({ disparo: d, lista: [], total: 0 });
    } finally {
      setVisualizaramLoading(false);
    }
  }, []);

  const loadMoreVisualizaram = useCallback(async () => {
    if (!visualizaramDisparo || visualizaramLoadingMore) return;
    if (visualizaramDisparo.lista.length >= visualizaramDisparo.total) return;
    setVisualizaramLoadingMore(true);
    try {
      const { data } = await disparosApi.getVisualizaram(visualizaramDisparo.disparo.id, visualizaramDisparo.lista.length, 15);
      setVisualizaramDisparo(prev => prev ? { ...prev, lista: [...prev.lista, ...data.items] } : prev);
    } catch {}
    finally { setVisualizaramLoadingMore(false); }
  }, [visualizaramDisparo, visualizaramLoadingMore]);

  const openNaoEntregues = useCallback(async (d: Disparo) => {
    setNaoEntreguesLoading(true);
    setNaoEntreguesDisparo({ disparo: d, lista: [] });
    try {
      const { data } = await disparosApi.getNaoEntregues(d.id);
      setNaoEntreguesDisparo({ disparo: d, lista: data });
    } catch {
      setNaoEntreguesDisparo({ disparo: d, lista: [] });
    } finally {
      setNaoEntreguesLoading(false);
    }
  }, []);

  // search historico
  const [search, setSearch] = useState('');

  const [readStats, setReadStats] = useState<DispatchReadStats>({});
  const [replyStats, setReplyStats] = useState<DispatchReplyStats>({});
  const [sessoesSelecionadas, setSessoesSelecionadas] = useState<number[]>([]);
  const [sessaoSearch, setSessaoSearch] = useState('');
  const sessoesDisponiveis = useMemo(
    () => sessions
      .filter(s => s.status === 'connected' && (s.statusDisparo ?? 'idle') !== 'disparando')
      .map(s => s.id),
    [sessions]
  );
  const toggleSessao = (id: number) => {
    setSessoesSelecionadas(prev => prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]);
  };
  const toggleTodasSessoes = () => {
    setSessoesSelecionadas(prev => prev.length === sessoesDisponiveis.length ? [] : sessoesDisponiveis);
  };
  useEffect(() => {
    setSessoesSelecionadas(prev => {
      const stillAvailable = prev.filter(id => sessoesDisponiveis.includes(id));
      if (stillAvailable.length > 0) return stillAvailable;
      return sessoesDisponiveis;
    });
  }, [sessoesDisponiveis]);
  const [templateAplicado, setTemplateAplicado] = useState<Template | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateDropOpen, setTemplateDropOpen] = useState(false);
  const templatesFiltrados = templates.filter(t =>
    t.titulo.toLowerCase().includes(templateSearch.toLowerCase()) ||
    t.conteudo.toLowerCase().includes(templateSearch.toLowerCase())
  );

  const socketRef = useRef<Socket | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const listaInputRef = useRef<HTMLInputElement | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [sRes, bRes, dRes, tRes] = await Promise.all([
        sessionsApi.list(),
        basesApi.list(),
        disparosApi.list(),
        templatesApi.list(),
      ]);
      setSessions(sRes.data.filter(s => s.status === 'connected'));
      setBases(bRes.data.filter(b => b.ativo));
      setHistorico(dRes.data);
      setTemplates(tRes.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadAll();
    socketRef.current = io(API_URL, { transports: ['websocket'] });
    socketRef.current.on('dispatch:progress', (data: DispatchProgress) => {
      setProgress(data);
      if (data.sent + data.failed >= data.total && data.total > 0) {
        if (data.disparoId) {
          setHistorico(prev => prev.map(d =>
            d.id === data.disparoId
              ? {
                  ...d,
                  totalEnviados: data.sent,
                  totalFalhas: data.failed,
                  status: data.pending > 0 ? ('pausado' as any) : ('concluido' as any),
                }
              : d
          ));
          setDisparoAtivo(prev => prev?.id === data.disparoId
            ? {
                ...prev,
                totalEnviados: data.sent,
                totalFalhas: data.failed,
                status: data.pending > 0 ? ('pausado' as any) : ('concluido' as any),
              }
            : prev
          );
        }
        loadAll();
      }
    });
    socketRef.current.on('dispatch:reply', (data: { dispatchId: string; phone: string; totalRespostas: number; textoResposta?: string }) => {
      const id = parseInt(data.dispatchId.replace('disparo_', ''), 10);
      setReplyStats(prev => ({ ...prev, [id]: data.totalRespostas }));
      setHistorico(prev => prev.map(d => d.id === id ? { ...d, totalRespostas: data.totalRespostas } : d));
      // Adiciona em tempo real no modal de respostas se estiver aberto
      if (data.textoResposta) {
        setRespostasDisparo(prev => {
          if (!prev || prev.disparo.id !== id) return prev;
          const nova: Resposta = {
            id: Date.now(),
            disparoId: id,
            phone: data.phone,
            sessionId: 0,
            textoResposta: data.textoResposta!,
            tipoResposta: 'mensagem',
            receivedAt: new Date().toISOString(),
          };
          return { ...prev, lista: [nova, ...prev.lista] };
        });
      }
    });
    socketRef.current.on('message:status', (data: MessageStatusEvent) => {
      setReadStats(prev => {
        const key = data.dispatchId;
        const cur = prev[key] ?? { delivered: 0, read: 0 };
        if (data.status === 'delivered') {
          return { ...prev, [key]: { ...cur, delivered: cur.delivered + 1 } };
        } else {
          // 'read' (2 tiques azuis): remove do balde de cinzas e adiciona ao de azuis
          return { ...prev, [key]: { delivered: Math.max(0, cur.delivered - 1), read: cur.read + 1 } };
        }
      });
      // Atualiza historico em tempo real ao receber visualizaÃ§Ã£o/entrega
      if (data.status === 'read') {
        const id = parseInt(data.dispatchId.replace('disparo_', ''), 10);
        setHistorico(prev => prev.map(d =>
          d.id === id ? { ...d, totalRespostas: d.totalRespostas } : d
        ));
      }
    });
    return () => { socketRef.current?.disconnect(); };
  }, [loadAll]);

  useEffect(() => {
    if (reportDisparoId == null) return;

    loadReportStats(reportDisparoId);
    const interval = window.setInterval(() => {
      loadReportStats(reportDisparoId);
    }, 2000);

    return () => window.clearInterval(interval);
  }, [reportDisparoId, loadReportStats]);

  useEffect(() => {
    if (progressBarRef.current && progress) {
      const pct = Math.round(((progress.sent + progress.failed) / progress.total) * 100);
      progressBarRef.current.style.width = `${pct}%`;
    }
  }, [progress]);

  const openModal = () => {
    setNomeDisparo('');
    setBaseSelecionada(null);
    setBaseSearch('');
    setBaseDropOpen(false);
    setIntervaloMin(10);
    setIntervaloMax(15);
    setMensagem('');
    setBotoes([]);
    setNovoBotao('');
    setNovoBotaoLink('');
    setNovoBotaoRetorno('');
    setModoBotao('normal');
    setImagemUrl('');
    setUsarFoto(false);
    setUsarBotoes(false);
    setUsarModeloMensagem(false);
    setTemplateAplicado(null);
    setTemplateSearch('');
    setTemplateDropOpen(false);
    setLimiteDiario(0);
    setAgendar(false);
    setDataAgendamento('');
    setNumeroTeste('');
    setCnpjTeste('');
    setCategoriaTeste('');
    setTesteOpen(false);
    setImagemUrl('');
    setError('');
    setModalOpen(true);
  };

  const aplicarModeloMensagem = useCallback((template: Template) => {
    setTemplateAplicado(template);
    setTemplateSearch(template.titulo);
    setMensagem(template.conteudo ?? '');
    setImagemUrl(template.imagemUrl ?? '');
    setBotoes(template.botoes ?? []);
    setUsarFoto(!!template.imagemUrl);
    setUsarBotoes((template.botoes?.length ?? 0) > 0);
    setUsarModeloMensagem(true);
    setTemplateDropOpen(false);
    setError('');
  }, []);

  const handleUploadImagem = async (file: File | null) => {
    if (!file) return;
    setEnviandoImagem(true);
    setError('');
    try {
      const { data } = await templatesApi.uploadImage(file);
      setImagemUrl(data.url);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao enviar imagem');
    } finally {
      setEnviandoImagem(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleImportarLista = async () => {
    if (!arquivoListaNova) {
      setError('Selecione um arquivo .xlsx ou .csv');
      return;
    }

    setImportLoading(true);
    setError('');
    try {
      const nomeGerado = `Lista ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(':', '-')}`;
      const formData = new FormData();
      formData.append('nome_base', nomeGerado);
      formData.append('file', arquivoListaNova);
      await api.post('/bases', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { data } = await basesApi.list();
      const basesAtivas = data.filter(b => b.ativo);
      setBases(basesAtivas);
      const baseCriada = basesAtivas.find(b => b.nomeBase === nomeGerado)
        ?? [...basesAtivas].sort((a, b) => b.id - a.id)[0]
        ?? null;
      if (baseCriada) setBaseSelecionada(baseCriada);
      setNomeListaNova('');
      setArquivoListaNova(null);
      showToast('Lista importada com sucesso');
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao importar lista');
    } finally {
      setImportLoading(false);
    }
  };

  const garantirListaImportada = async (): Promise<Base | null> => {
    if (baseSelecionada) return baseSelecionada;
    if (!arquivoListaNova) return null;

    setImportLoading(true);
    setError('');
    try {
      const nomeGerado = `Lista ${new Date().toLocaleDateString('pt-BR')} ${new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }).replace(':', '-')}`;
      const formData = new FormData();
      formData.append('nome_base', nomeGerado);
      formData.append('file', arquivoListaNova);
      await api.post('/bases', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { data } = await basesApi.list();
      const basesAtivas = data.filter(b => b.ativo);
      setBases(basesAtivas);
      const baseCriada = basesAtivas.find(b => b.nomeBase === nomeGerado)
        ?? [...basesAtivas].sort((a, b) => b.id - a.id)[0]
        ?? null;
      if (baseCriada) setBaseSelecionada(baseCriada);
      setArquivoListaNova(null);
      return baseCriada;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao importar lista');
      return null;
    } finally {
      setImportLoading(false);
    }
  };

  const validate = () => {
    if (!baseSelecionada && !arquivoListaNova) return 'Selecione ou importe uma lista de telefones';
    if (!mensagem.trim()) return 'Digite a mensagem';
    if (sessoesSelecionadas.length === 0) return 'Selecione pelo menos uma sessão para essa campanha';
    if (intervaloMin === '' || intervaloMin < 1) return 'Intervalo minimo deve ser maior que 1 segundo';
    if (intervaloMax === '' || intervaloMax < intervaloMin) return 'O tempo final deve ser maior ou igual ao inicial';
    if (agendar) {
      if (!dataAgendamento) return 'Informe a data e hora do agendamento';
      if (new Date(dataAgendamento).getTime() <= Date.now()) return 'A data do agendamento deve ser no futuro';
    }
    return '';
  };

  const criarDisparoFinal = async (basePronta: Base, sessoesIds: number[], contatos: string[]) => {
    setLoading(true);
    try {
      await disparosApi.create({
        nomeDisparo: nomeDisparo.trim() || `Envio ${new Date().toLocaleDateString('pt-BR')}`,
        baseId: basePronta.id,
        baseNome: basePronta.nomeBase,
        categoriaNome: basePronta.categoria?.nome ?? null,
        contatos,
        sessoesIds,
        intervaloMin: intervaloMin === '' ? 1 : intervaloMin,
        intervaloMax: intervaloMax === '' ? 3 : intervaloMax,
        mensagem: mensagem.trim(),
        imagemUrl: imagemUrl.trim() || null,
        botoes: botoes.length > 0 ? botoes : undefined,
        agendadoPara: agendar && dataAgendamento ? new Date(dataAgendamento).toISOString() : null,
        cnpjManual: null,
      });
      setModalOpen(false);
      loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao criar disparo');
    } finally {
      setLoading(false);
    }
  };

  const handleDisparo = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setLoading(true);
    try {
      const basePronta = await garantirListaImportada();
      if (!basePronta) { setLoading(false); return; }

      const contatos = basePronta.contatos.map(c => c.telefone);
      await criarDisparoFinal(basePronta, sessoesSelecionadas, contatos);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao criar disparo');
      setLoading(false);
    }
  };

  const handleIniciar = async (d: Disparo) => {
    // Atualiza status otimisticamente
    setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'em_andamento' as any } : x));
    setProgress({ disparoId: d.id, total: d.totalContatos, sent: d.totalEnviados, pending: d.totalContatos - d.totalEnviados - d.totalFalhas, failed: d.totalFalhas });
    try {
      await disparosApi.iniciar(d.id);
    } catch (e: any) {
      // Reverte em caso de erro
      setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'pendente' as any } : x));
      setProgress(null);
      alert(e?.response?.data?.message || 'Erro ao iniciar disparo');
    }
  };

  const handlePausar = async (d: Disparo) => {
    // Atualiza status otimisticamente
    setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'pendente' as any } : x));
    try {
      await disparosApi.pausar(d.id);
    } catch (e: any) {
      setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'em_andamento' as any } : x));
      alert(e?.response?.data?.message || 'Erro ao pausar');
    }
  };

  const handleRetomar = async (d: Disparo) => {
    setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'em_andamento' as any } : x));
    try {
      await disparosApi.retomar(d.id);
    } catch (e: any) {
      setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'pendente' as any } : x));
      alert(e?.response?.data?.message || 'Erro ao retomar');
    }
  };

  const handleReenviar = async (d: Disparo) => {
    setConfirmReenviar(null);
    // Reseta stats de leitura deste disparo (o backend vai resetar as mensagens)
    setReadStats(prev => { const n = { ...prev }; delete n[`disparo_${d.id}`]; return n; });
    setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: 'em_andamento' as any, totalEnviados: 0, totalFalhas: 0, totalReenvios: (x.totalReenvios ?? 0) + 1 } : x));
    setProgress({ disparoId: d.id, total: d.totalContatos, sent: 0, pending: d.totalContatos, failed: 0 });
    try {
      await disparosApi.reenviar(d.id);
    } catch (e: any) {
      setHistorico(prev => prev.map(x => x.id === d.id ? { ...x, status: d.status } : x));
      setProgress(null);
      alert(e?.response?.data?.message || 'Erro ao reenviar');
    }
  };

  const handleCancelar = async () => {
    if (!disparoAtivo) return;
    try {
      await disparosApi.cancelar(disparoAtivo.id);
      setProgressModalOpen(false);
      setDisparoAtivo(null);
      setProgress(null);
      loadAll();
    } catch {}
  };

  const progressPercent = progress
    ? Math.round(((progress.sent + progress.failed) / progress.total) * 100)
    : 0;

  const concluido = progress && progress.total > 0 && progress.sent + progress.failed >= progress.total;

  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-md px-4 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-300 transition-all";

  const basesFiltradas = useMemo(() =>
    bases.filter(b => !baseSearch.trim() || b.nomeBase.toLowerCase().includes(baseSearch.toLowerCase())),
    [bases, baseSearch]
  );

  const filtered = historico.filter(d =>
    !search.trim() || d.nomeDisparo.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">

      {!modalOpen && (
        <>
          <div className="bg-white border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={openModal}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-btn transition-opacity hover:opacity-80 flex-shrink-0"
              style={{ background: '#22C55E', color: '#FFFFFF' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Novo Envio
            </button>
            <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 w-64 bg-white">
              <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Pesquisar envio..."
                className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-white border border-gray-200 px-6 py-16 flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(37, 99, 235, 0.15)' }}>
                <svg className="w-8 h-8" fill="none" stroke="#3B82F6" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </div>
              <p className="text-base font-semibold text-gray-900">
                {historico.length === 0 ? 'Nenhum disparo realizado' : 'Nenhum disparo encontrado'}
              </p>
              <p className="text-sm text-gray-500 mt-1 max-w-sm">
                {historico.length === 0
                  ? 'Crie seu primeiro envio em massa para começar a disparar mensagens.'
                  : 'Tente outro termo na busca.'}
              </p>
              {historico.length === 0 && (
                <button
                  type="button"
                  onClick={openModal}
                  className="mt-5 flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-btn transition-opacity hover:opacity-80"
                  style={{ background: '#22C55E', color: '#FFFFFF' }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  Novo Envio
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto bg-white">
            <div>
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Nome disparo</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Total da base</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Enviados</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Falhas</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Status</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Data</th>
                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filtered.map((d) => {
                  const s = STATUS_LABEL[d.status] ?? STATUS_LABEL['pendente'];
                  const isAtivo = progress && progress.disparoId === d.id;
                  const enviados = isAtivo ? progress.sent : d.totalEnviados;
                  const falhas = isAtivo ? progress.failed : d.totalFalhas;
                  const total = d.totalContatos;
                  const pct = total > 0 ? Math.min(100, Math.round(((enviados + falhas) / total) * 100)) : 0;
                  const emAndamento = d.status === 'em_andamento';
                  const concluido = d.status === 'concluido';
                  const cancelado = d.status === 'cancelado';
                  const barColor = cancelado ? 'bg-red-400' : concluido ? 'bg-emerald-500' : 'bg-blue-500';

                  return (
                    <tr key={d.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                      <td className="px-4 py-3 max-w-[220px]">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 truncate font-medium" title={d.nomeDisparo}>{d.nomeDisparo}</span>
                          {(d.totalReenvios ?? 0) > 0 && (
                            <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-yellow-50 border border-[rgba(245,158,11,0.3)] text-yellow-700 font-medium">{d.totalReenvios}x</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{total.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{enviados.toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 text-center">{falhas.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">
                        {emAndamento ? (
                          <div className="flex flex-col gap-1 w-36 mx-auto">
                            <div className="flex justify-between items-center text-[10px] text-gray-400 mb-0.5">
                              <span className="text-yellow-700 font-semibold animate-pulse">Disparando...</span>
                              <span>{pct}%</span>
                            </div>
                            <div className="flex justify-between text-[10px] text-gray-400">
                              <span>{enviados + falhas} / {total}</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-1">
                              <div className={`h-1 rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        ) : (
                          <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${s.color}`}>{s.label}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 text-center whitespace-nowrap">{fmtDate(d.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              if (emAndamento) { handlePausar(d); return; }
                              if (d.status === 'pausado' || d.status === 'limite_atingido') { handleRetomar(d); return; }
                              if (d.status === 'pendente') {
                                handleIniciar(d);
                                return;
                              }
                              setConfirmReenviar(d);
                            }}
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 transition-all hover:bg-[rgba(59,130,246,0.2)]"
                            title={
                              emAndamento ? 'Pausar disparo' :
                              d.status === 'pausado' ? 'Retomar disparo' :
                              d.status === 'limite_atingido' ? 'Retomar disparo' :
                              d.status === 'pendente' ? (d.totalEnviados > 0 || d.totalFalhas > 0 ? 'Continuar disparo' : 'Iniciar disparo') :
                              'Reenviar disparo'
                            }
                          >
                            {emAndamento ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                              </svg>
                            ) : (d.status === 'pausado' || d.status === 'limite_atingido' || d.status === 'pendente') ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(d)}
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-500 transition-all hover:bg-[rgba(239,68,68,0.2)]"
                            title="Excluir disparo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            </div>
          )}
        </>
      )}

      {/* Modal Novo Envio — tela cheia */}
      {modalOpen && createPortal(
        <section className="fixed inset-0 z-[9999] bg-white flex flex-col overflow-hidden">

            {/* Header */}
            <div className="border-b border-gray-100 px-4 py-4 sm:px-6 lg:px-8 lg:py-5 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-center bg-gradient-to-r from-white via-gray-50 to-white flex-shrink-0">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Novo Envio</h3>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleDisparo}
                  disabled={loading}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-2 rounded-md bg-emerald-500 px-4 sm:px-8 py-3 text-sm font-semibold text-white transition-all hover:bg-emerald-600 disabled:opacity-20 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Criando...
                    </>
                  ) : (
                    agendar ? 'Agendar envio' : 'Criar envio'
                  )}
                </button>
                <button type="button" onClick={() => setModalOpen(false)} className="w-10 h-10 flex items-center justify-center rounded-lg border text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-all">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            </div>

            {/* ConteÃºdo */}
            <div className="flex-1 overflow-y-auto space-y-6 px-4 py-4 sm:px-6 lg:px-8 lg:py-6 bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.06),_transparent_28%),linear-gradient(180deg,#ffffff_0%,#fbfbfb_100%)]">

              <div className="grid grid-cols-1 xl:grid-cols-[1fr_1.35fr_0.85fr] gap-4 items-start">
                <div className="min-w-0 space-y-4">
                  <div>
                    <label className="block text-sm text-gray-900 mb-1 font-semibold">Importe a lista de telefones *</label>
                    <div className="border border-gray-200 bg-gray-50 p-3 space-y-2.5">
                      {baseSelecionada && (
                        <div className="border border-emerald-200 bg-emerald-50 px-3 py-2">
                          <p className="text-xs font-semibold text-emerald-700">Lista pronta para envio</p>
                          <p className="text-xs text-emerald-700/80 mt-0.5">
                            {baseSelecionada.nomeBase} com {baseSelecionada.totalContatos.toLocaleString('pt-BR')} numeros
                          </p>
                        </div>
                      )}
                      <div
                        onClick={() => listaInputRef.current?.click()}
                        onMouseDown={(e) => e.preventDefault()}
                        className="w-full border border-dashed border-gray-300 rounded-md px-3 py-3 flex flex-col items-center gap-1.5 cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all"
                      >
                        {arquivoListaNova ? (
                          <span className="text-xs text-emerald-700 font-medium">{arquivoListaNova.name}</span>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="text-xs text-gray-900 font-medium">Clique para selecionar o arquivo .xlsx ou .csv</span>
                          </>
                        )}
                      </div>
                      <input
                        ref={listaInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={(e) => { setArquivoListaNova(e.target.files?.[0] || null); setError(''); }}
                      />
                      <p className="text-xs font-semibold" style={{ color: '#DC2626' }}>
                        O arquivo deve ter apenas a coluna Telefone, TELEFONE ou telefone.
                      </p>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        Para usar variáveis (ex.: <span className="font-mono text-gray-800">&#123;nome&#125;</span>), adicione colunas extras no Excel e crie um modelo em{' '}
                        <Link to="/templates" className="font-semibold text-blue-600 underline hover:text-blue-700">
                          Modelos de Mensagens
                        </Link>.
                      </p>
                      <button
                        type="button"
                        onClick={handleImportarLista}
                        disabled={importLoading || !arquivoListaNova}
                        className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md text-xs font-semibold transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        {importLoading ? 'Importando lista...' : 'Importar lista agora'}
                      </button>
                    </div>
                  </div>
                  <div className="border p-3 space-y-3 bg-white border border-gray-200">
                    <label className="block text-sm text-gray-900 mb-1 font-semibold">Intervalo entre envios</label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        value={intervaloMin}
                        title="Minimo"
                        placeholder="10"
                        onChange={e => setIntervaloMin(e.target.value === '' ? '' : Math.max(1, +e.target.value))}
                        className="w-20 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 text-center focus:outline-none focus:border-gray-400 transition-all bg-white"
                      />
                      <span className="text-gray-300 text-sm">ate</span>
                      <input
                        type="number"
                        min={intervaloMin === '' ? 1 : intervaloMin}
                        value={intervaloMax}
                        title="Maximo"
                        placeholder="15"
                        onChange={e => setIntervaloMax(e.target.value === '' ? '' : +e.target.value)}
                        className="w-20 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 text-center focus:outline-none focus:border-gray-400 transition-all bg-white"
                      />
                      <span className="text-sm text-gray-900 font-medium">segundos</span>
                    </div>
                    <p className="mt-1 text-xs text-gray-900 font-medium">Padrao recomendado: 10 a 15 segundos.</p>
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="border p-3 flex flex-col gap-3 bg-white border border-gray-200">
                    <label className="block text-sm text-gray-900 font-semibold">Mensagem *</label>
                    <textarea
                      value={mensagem}
                      onChange={e => { setMensagem(e.target.value); setError(''); }}
                      rows={5}
                      placeholder="Digite aqui o texto da mensagem"
                      className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all resize-none bg-white"
                    />
                    <div className="flex flex-col gap-2">
                      {canUseModelosMensagem && (
                      <button
                        type="button"
                        onClick={() => {
                          setUsarModeloMensagem(prev => {
                            const next = !prev;
                            if (!next) {
                              setTemplateAplicado(null);
                              setTemplateSearch('');
                              setTemplateDropOpen(false);
                            }
                            return next;
                          });
                        }}
                        className={`flex items-center justify-between border px-2.5 py-2 text-left transition-all ${usarModeloMensagem ? 'border-emerald-300 bg-emerald-50' : ' hover:border-gray-300'}`}
                      >
                        <span className="text-sm font-medium text-gray-700">Modelo de mensagem</span>
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full border transition-all ${usarModeloMensagem ? 'bg-emerald-500 border-emerald-500' : 'bg-gray-200 border-gray-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full shadow-sm transition-transform ${usarModeloMensagem ? 'translate-x-4' : 'translate-x-0.5'} bg-white border border-gray-200`} />
                        </span>
                      </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setAgendar(prev => {
                            const next = !prev;
                            if (!next) setDataAgendamento('');
                            return next;
                          });
                        }}
                        className={`flex items-center justify-between border px-2.5 py-2 text-left transition-all ${agendar ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'}`}
                      >
                        <span className="text-sm font-medium text-gray-700">Agendar envio</span>
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full border transition-all ${agendar ? 'bg-emerald-500 border-emerald-500' : 'bg-gray-200 border-gray-300'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full shadow-sm transition-transform ${agendar ? 'translate-x-4' : 'translate-x-0.5'} bg-white border border-gray-200`} />
                        </span>
                      </button>
                    </div>
                    {agendar && (() => {
                      const [dataParte, horaParte] = dataAgendamento ? dataAgendamento.split('T') : ['', ''];
                      const [hh, mm] = horaParte ? horaParte.split(':') : ['', ''];
                      const hojeIso = (() => {
                        const d = new Date();
                        const tz = d.getTimezoneOffset() * 60000;
                        return new Date(d.getTime() - tz).toISOString().slice(0, 10);
                      })();
                      const setarHora = (novaHh: string, novoMm: string) => {
                        const h = novaHh || '09';
                        const m = novoMm || '00';
                        const d = dataParte || hojeIso;
                        setDataAgendamento(`${d}T${h}:${m}`);
                      };
                      const setarData = (novaData: string) => {
                        const h = hh || '09';
                        const m = mm || '00';
                        setDataAgendamento(novaData ? `${novaData}T${h}:${m}` : '');
                      };
                      return (
                        <div className="border border-emerald-200 bg-emerald-50 p-3 space-y-2.5">
                          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                            <div>
                              <label className="block text-[11px] text-emerald-800 mb-1 font-semibold uppercase tracking-wide">Dia</label>
                              <input
                                type="date"
                                value={dataParte}
                                min={hojeIso}
                                onChange={e => setarData(e.target.value)}
                                className="w-full border border-emerald-200 rounded-md px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-emerald-400 transition-all bg-white"
                              />
                            </div>
                            <div>
                              <label className="block text-[11px] text-emerald-800 mb-1 font-semibold uppercase tracking-wide">Hora</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder="HH"
                                  value={hh}
                                  onChange={e => {
                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                                    const n = parseInt(digits, 10);
                                    if (digits === '' || (!Number.isNaN(n) && n <= 23)) {
                                      setarHora(digits, mm);
                                    }
                                  }}
                                  className="w-12 border border-emerald-200 rounded-md px-2 py-2 text-sm text-gray-900 text-center focus:outline-none focus:border-emerald-400 bg-white"
                                />
                                <span className="text-emerald-800 font-bold">:</span>
                                <input
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={2}
                                  placeholder="MM"
                                  value={mm}
                                  onChange={e => {
                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 2);
                                    const n = parseInt(digits, 10);
                                    if (digits === '' || (!Number.isNaN(n) && n <= 59)) {
                                      setarHora(hh, digits);
                                    }
                                  }}
                                  className="w-12 border border-emerald-200 rounded-md px-2 py-2 text-sm text-gray-900 text-center focus:outline-none focus:border-emerald-400 bg-white"
                                />
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="text-[11px] text-emerald-800 font-semibold">Rápido:</span>
                            {[
                              { label: 'Em 1h', minutos: 60 },
                              { label: 'Em 3h', minutos: 180 },
                              { label: 'Amanhã 9h', minutos: -1 },
                            ].map(p => (
                              <button
                                key={p.label}
                                type="button"
                                onClick={() => {
                                  let alvo: Date;
                                  if (p.minutos === -1) {
                                    alvo = new Date();
                                    alvo.setDate(alvo.getDate() + 1);
                                    alvo.setHours(9, 0, 0, 0);
                                  } else {
                                    alvo = new Date(Date.now() + p.minutos * 60000);
                                  }
                                  const tz = alvo.getTimezoneOffset() * 60000;
                                  setDataAgendamento(new Date(alvo.getTime() - tz).toISOString().slice(0, 16));
                                }}
                                className="text-[11px] px-2 py-1 rounded border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-100 transition-colors font-medium"
                              >
                                {p.label}
                              </button>
                            ))}
                          </div>
                          {dataAgendamento && (
                            <p className="text-xs text-emerald-800 font-medium">
                              Será enviado em <span className="font-bold">{new Date(dataAgendamento).toLocaleString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                            </p>
                          )}
                        </div>
                      );
                    })()}
                    {canUseModelosMensagem && usarModeloMensagem && (
                      <div className="border border-emerald-200 bg-emerald-50 p-2.5 space-y-2.5">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-emerald-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V19a2 2 0 01-2 2zM13 3v4a1 1 0 001 1h4" />
                          </svg>
                          <span className="text-sm font-semibold text-emerald-700">Selecionar modelo salvo</span>
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            value={templateSearch}
                            onFocus={() => setTemplateDropOpen(true)}
                            onChange={e => { setTemplateSearch(e.target.value); setTemplateDropOpen(true); }}
                            placeholder="Pesquisar modelo de mensagem..."
                            className="w-full border border-emerald-200 rounded-md px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-all bg-white border border-gray-200"
                          />
                          {templateDropOpen && (
                            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-emerald-200 shadow-lg bg-white border border-gray-200">
                              {templatesFiltrados.length === 0 ? (
                                <div className="px-3 py-2 text-xs text-gray-400">Nenhum modelo encontrado.</div>
                              ) : templatesFiltrados.map(t => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onClick={() => aplicarModeloMensagem(t)}
                                  className="w-full text-left px-3 py-2 border-b last:border-b-0 border-gray-100 hover:bg-emerald-50 transition-all"
                                >
                                  <p className="text-sm font-medium text-gray-900">{t.titulo}</p>
                                  <p className="text-xs text-gray-400 truncate">{t.conteudo}</p>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {templateAplicado && (
                          <p className="text-xs text-emerald-700">
                            Modelo aplicado: <span className="font-semibold">{templateAplicado.titulo}</span>. As variáveis como <code>{'{nome}'}</code> e <code>{'{valor}'}</code> serão buscadas nas colunas da planilha importada.
                          </p>
                        )}
                      </div>
                    )}
                    {usarFoto && (
                      <div className="border border-emerald-200 bg-emerald-50 p-2.5 space-y-2.5">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-emerald-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-10h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm font-semibold text-emerald-700">Foto da mensagem</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={() => imageInputRef.current?.click()}
                            className="px-3 py-2 border border-emerald-300 text-emerald-700 rounded-md text-xs font-semibold hover:bg-emerald-50 transition-all"
                          >
                            {enviandoImagem ? 'Enviando foto...' : (imagemUrl ? 'Trocar foto' : 'Escolher foto')}
                          </button>
                          {imagemUrl && (
                            <button
                              type="button"
                              onClick={() => setImagemUrl('')}
                              className="px-3 py-2 border border-red-200 text-red-500 rounded-md text-xs font-semibold hover:bg-red-50 transition-all"
                            >
                              Remover
                            </button>
                          )}
                        </div>
                        {imagemUrl && (
                          <div className="flex items-center gap-2 rounded-md border border-emerald-200 px-3 py-2 bg-white border border-gray-200">
                            <svg className="w-4 h-4 text-emerald-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-10h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-700 truncate">{imagemUrl.split('/').pop()}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => handleUploadImagem(e.target.files?.[0] || null)}
                    />
                    {usarBotoes && (
                      <div className="border border-emerald-200 bg-emerald-50 p-2.5 space-y-2.5">
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={() => setModoBotao('normal')}
                            className={`px-3 py-2 border rounded-md text-xs font-semibold transition-all ${modoBotao === 'normal' ? 'bg-emerald-500 border-emerald-600 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          >
                            Botao normal
                          </button>
                          <button
                            type="button"
                            onClick={() => setModoBotao('link')}
                            className={`px-3 py-2 border rounded-md text-xs font-semibold transition-all ${modoBotao === 'link' ? 'bg-emerald-500 border-emerald-600 text-white' : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}
                          >
                            Botao com link
                          </button>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div>
                            <label className="block text-[11px] text-emerald-700 mb-1">Nome do botão</label>
                            <input
                              type="text"
                              value={novoBotao}
                              onChange={e => setNovoBotao(e.target.value)}
                              placeholder="Texto do botao"
                              className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white"
                            />
                          </div>
                          {modoBotao === 'link' && (
                            <div>
                              <label className="block text-[11px] text-emerald-700 mb-1">Link do botão</label>
                              <input
                                type="text"
                                value={novoBotaoLink}
                                onChange={e => setNovoBotaoLink(e.target.value)}
                                placeholder="https://..."
                                className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white"
                              />
                            </div>
                          )}
                          {modoBotao === 'normal' && (
                            <div>
                              <label className="block text-[11px] text-emerald-700 mb-1">Mensagem de retorno</label>
                              <input
                                type="text"
                                value={novoBotaoRetorno}
                                onChange={e => setNovoBotaoRetorno(e.target.value)}
                                placeholder="Mensagem de retorno ao clicar"
                                className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white"
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              const texto = novoBotao.trim();
                              if (!texto || botoes.length >= 3) return;
                              if (modoBotao === 'link') {
                                const url = novoBotaoLink.trim();
                                if (!url) return;
                                setBotoes(prev => [...prev, { texto, url }]);
                                setNovoBotaoLink('');
                              } else {
                                const mensagemRetorno = novoBotaoRetorno.trim();
                                if (!mensagemRetorno) return;
                                setBotoes(prev => [...prev, { texto, mensagemRetorno }]);
                              }
                              setNovoBotao('');
                              setNovoBotaoRetorno('');
                            }}
                            className="px-3 py-2 border border-emerald-600 bg-emerald-500 text-white rounded-md text-xs font-semibold hover:bg-emerald-600 transition-all"
                          >
                            Adicionar
                          </button>
                        </div>
                        {modoBotao === 'normal' && (
                          <p className="text-[11px] text-emerald-700">
                            Quando o lead clicar no botão, essa mensagem será enviada automaticamente no WhatsApp.
                          </p>
                        )}
                        {botoes.length > 0 && (
                          <div className="flex flex-col gap-2">
                            {botoes.map((botao, index) => (
                              <button
                                key={`${botao.texto}-${index}`}
                                type="button"
                                onClick={() => setBotoes(prev => prev.filter((_, i) => i !== index))}
                                title={botao.url ? botao.url : (botao.mensagemRetorno || '')}
                                className="px-3 py-2 rounded-md border border-emerald-200 text-left text-[11px] text-emerald-700 bg-white border border-gray-200"
                              >
                                {botao.url ? `Nome do botão: ${botao.texto} | Link: ${botao.url} | remover` : `Nome do botão: ${botao.texto} | Mensagem de retorno: ${botao.mensagemRetorno} | remover`}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="border p-3 space-y-3 bg-white border border-gray-200">
                    <div className="overflow-hidden border border-gray-200 bg-[#eae6df] shadow-sm">
                      <div className="p-2.5 flex flex-col gap-2 min-h-[300px] justify-end">
                        <div className="overflow-hidden shadow-sm w-full max-w-[260px] bg-white border border-gray-200">
                          {imagemUrl && <img src={imagemUrl.startsWith('/uploads') ? `${API_URL}${imagemUrl}` : imagemUrl} alt="imagem" onError={e => (e.currentTarget.style.display = 'none')} className="w-full max-h-36 object-contain bg-gray-50" />}
                          <div className="px-2 pt-1.5 pb-0.5"><p className="text-[10px] text-gray-900 line-clamp-8 leading-relaxed">{mensagem || 'Sua mensagem vai aparecer aqui.'}</p></div>
                          <div className="px-2 pb-1.5 flex justify-end"><span className="text-[9px] text-gray-400">{new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span></div>
                        </div>
                        {botoes.map((b, i) => (
                          <div key={i} className="rounded-lg flex items-center justify-center gap-1 px-2 py-1.5 shadow-sm w-full max-w-sm bg-white border border-gray-200">
                            {b.url
                              ? <svg className="w-3 h-3 text-[#0070ba] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                              : <svg className="w-3 h-3 text-[#0070ba] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>}
                            <span className="text-[10px] text-[#0070ba] font-medium truncate">{b.texto}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SeÃ§Ã£o 4+5 â€" Conta que envia + mensagem */}
              <div className="space-y-4">

                {/* Esquerda â€" SessÃµes */}
                <div className="min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <p className="sr-only">Rodizio automatico das contas</p>
                    {sessions.length > 1 && (
                      <button type="button" onClick={toggleTodasSessoes} className="text-xs text-blue-500 hover:text-blue-700 font-medium transition-colors">
                        Rodizio automatico
                      </button>
                    )}
                    {sessoesSelecionadas.length > 0 && <span className="text-[11px] text-emerald-700 font-semibold">{sessoesSelecionadas.length} prontas</span>}
                  </div>
                  {sessions.length > 1 && (
                    <div className="relative mb-2">
                      <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
                      </svg>
                      <input type="text" value={sessaoSearch} onChange={e => setSessaoSearch(e.target.value)}
                        placeholder="Filtrar por nome ou nÃºmero..."
                        className="w-full border rounded-md pl-8 pr-3 py-1.5 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-400 transition-all bg-white border border-gray-200"
                      />
                    </div>
                  )}
                  {sessions.length > 0 && (
                    <div className="grid grid-cols-1 gap-1.5">
                      {sessions
                        .filter(s => {
                          const q = sessaoSearch.toLowerCase();
                          return !q || s.nomeSessao.toLowerCase().includes(q) || (s.numeroWhatsapp ?? '').includes(q);
                        })
                        .map(s => {
                          const sel = sessoesSelecionadas.includes(s.id);
                          const emDisparo = (s.statusDisparo ?? 'idle') === 'disparando';
                          return (
                            <button key={s.id} type="button"
                              disabled={emDisparo}
                              onClick={() => { if (!emDisparo) { toggleSessao(s.id); setError(''); } }}
                              title={emDisparo ? 'SessÃ£o em uso â€" disparando no momento' : undefined}
                              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${emDisparo ? 'bg-gray-50 opacity-50 cursor-not-allowed' : sel ? 'bg-emerald-50 border-emerald-300' : ' hover:border-gray-300'}`}>
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${sel ? 'bg-[rgba(34,197,94,0.2)]' : 'bg-gray-100'}`}>
                                <svg className={`w-3.5 h-3.5 ${sel ? 'text-emerald-700' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0 text-left">
                                <p className={`text-xs font-semibold truncate ${sel ? 'text-emerald-700' : 'text-gray-700'}`}>{s.nomeSessao}</p>
                                {s.numeroWhatsapp && <p className="text-[10px] text-gray-400 truncate font-mono">{s.numeroWhatsapp}</p>}
                              </div>
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${sel ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300'}`}>
                                {sel && <svg className="w-2.5 h-2.5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>

              </div>

              {/* Erro */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}
            </div>
        </section>,
        document.body
      )}

      {/* Modal Progresso */}
      {progressModalOpen && disparoAtivo && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-2xl shadow-xl w-full max-w-md bg-white border border-gray-200">

            <div className="px-7 py-5 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-base font-bold text-gray-900">{disparoAtivo.nomeDisparo}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">{disparoAtivo.baseNome} Â· {disparoAtivo.totalContatos.toLocaleString()} contatos</p>
                </div>
                {!concluido ? (
                  <span className="text-xs border border-[rgba(245,158,11,0.3)] px-3 py-1 rounded-full font-semibold animate-pulse flex-shrink-0 bg-yellow-50 text-yellow-700">
                    {disparoAtivo.agendadoPara && new Date(disparoAtivo.agendadoPara) > new Date() ? 'Agendado' : 'Em andamento'}
                  </span>
                ) : (
                  <span className="text-xs border border-emerald-200 px-3 py-1 rounded-full font-semibold flex-shrink-0 bg-emerald-50 text-emerald-700">
                    ConcluÃ­do
                  </span>
                )}
              </div>
            </div>

            <div className="px-7 py-5 space-y-4">
              {/* Barra de progresso */}
              <div>
                <div className="flex justify-between text-xs text-gray-400 mb-2">
                  <span>{progress ? progress.sent + progress.failed : 0} / {progress?.total ?? 0}</span>
                  <span className="font-bold text-gray-600">{progressPercent}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    ref={progressBarRef}
                    className={`h-2 rounded-full transition-all duration-500 ${concluido ? 'bg-emerald-500' : 'bg-blue-500'}`}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { label: 'Total', value: progress?.total ?? 0, color: 'text-gray-600' },
                  { label: 'Enviados', value: progress?.sent ?? 0, color: 'text-emerald-700' },
                  { label: 'Pendentes', value: progress?.pending ?? 0, color: 'text-gray-400' },
                  { label: 'Falhas', value: progress?.failed ?? 0, color: 'text-red-500' },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 border border-gray-100 rounded-xl p-3 text-center">
                    <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* SessÃµes em uso */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-400">SessÃµes:</span>
                {disparoAtivo.sessoesIds.map(sid => {
                  const s = sessions.find(x => x.id === sid);
                  const isActive = progress?.sessionUsed === sid;
                  return (
                    <span
                      key={sid}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-all ${isActive ? ' border-emerald-200 ' : 'bg-gray-100 border-gray-200 text-gray-400'} bg-emerald-50 text-emerald-700`}
                    >
                      {s?.nomeSessao ?? `#${sid}`}
                    </span>
                  );
                })}
              </div>

              {/* Contato atual */}
              {progress?.currentContact && !concluido && (
                <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl">
                  <p className="text-xs text-gray-400 mb-1">Enviando para:</p>
                  <p className="text-sm text-gray-600 font-mono truncate">{progress.currentContact}</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 px-7 py-5 border-t border-gray-100">
              {!concluido ? (
                <button
                  type="button"
                  onClick={handleCancelar}
                  className="flex-1 py-3 hover:bg-[rgba(239,68,68,0.2)] border border-red-200 font-semibold rounded-xl transition-all text-sm bg-red-50 text-red-600"
                >
                  Cancelar Disparo
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setProgressModalOpen(false); setDisparoAtivo(null); setProgress(null); }}
                  className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl transition-all hover:bg-gray-800 text-sm"
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Confirmar Reenvio */}
      {confirmReenviar && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-sm bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-sm font-bold text-gray-900">Confirmar reenvio</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 text-center">
                Deseja reenviar a mensagem para todos os <span className="font-semibold text-gray-900">{confirmReenviar.totalContatos.toLocaleString()} contatos</span>?
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-5">
              <button
                type="button"
                onClick={() => setConfirmReenviar(null)}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleReenviar(confirmReenviar)}
                className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold transition-all"
              >
                Reenviar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Confirmar ExclusÃ£o */}
      {confirmDelete && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-sm bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 border border-red-200 flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <h3 className="text-sm font-bold text-gray-900">Excluir disparo</h3>
              <p className="text-xs text-gray-500 mt-1">
                Tem certeza que deseja excluir <span className="font-semibold text-gray-700">"{confirmDelete.nomeDisparo}"</span>? Esta aÃ§Ã£o nÃ£o pode ser desfeita.
              </p>
            </div>
            <div className="flex gap-3 px-6 py-5">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-medium transition-all"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(confirmDelete)}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-semibold transition-all"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal RelatÃ³rio */}
      {reportDisparo && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-lg flex flex-col bg-white border border-gray-200">

            {/* CabeÃ§alho */}
            <div className="relative px-6 py-4 border-b border-gray-200 text-center">
              <h2 className="text-sm font-bold text-gray-900">{reportDisparo.nomeDisparo}</h2>
              <p className="text-xs text-gray-400 mt-0.5">{reportDisparo.baseNome ?? 'â€"'} Â· {fmt(reportDisparo.createdAt)}</p>
              <button
                type="button"
                title="Fechar relatÃ³rio"
                onClick={() => setReportDisparoId(null)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Metricas */}
            {(() => {
              const stats = readStats[`disparo_${reportDisparo.id}`];
              const dbStats = reportStats[reportDisparo.id];
              const grayTicks = stats?.delivered ?? 0;   // 2 tiques cinzas (entregue, não lido)
              const blueTicks = stats?.read ?? 0;        // 2 tiques azuis (visualizado)
              const delivered = grayTicks + blueTicks;   // total atual confirmado pelo banco
              const read = blueTicks;
              const enviados = reportDisparo.totalEnviados + (dbStats?.enviadosHistorico ?? reportDisparo.totalEnviadosHistorico ?? 0);
              const falhas = reportDisparo.totalFalhas + (dbStats?.falhasHistorico ?? reportDisparo.totalFalhasHistorico ?? 0);
              const naoEntregues = naoEntreguesStats[reportDisparo.id] ?? dbStats?.naoEntregues ?? 0;
              const respostas = replyStats[reportDisparo.id] ?? dbStats?.totalRespostas ?? reportDisparo.totalRespostas ?? 0;
              const reenvios = reportDisparo.totalReenvios ?? 0;

              const cards = [
                { label: 'Disparados', val: enviados, bg: 'bg-blue-50 border-[rgba(59,130,246,0.2)]', num: 'text-blue-700', sub: 'text-blue-500', icon: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8', iconCls: 'text-blue-700', iconBg: 'bg-[rgba(59,130,246,0.2)]', span: false, onClick: undefined },
                { label: 'Entregues', val: delivered, bg: 'bg-emerald-50 border-[rgba(34,197,94,0.2)]', num: 'text-emerald-700', sub: 'text-emerald-500', icon: 'M5 13l4 4L19 7', iconCls: 'text-emerald-700', iconBg: 'bg-[rgba(34,197,94,0.2)]', span: false, onClick: undefined },
                { label: 'N\u00ba n\u00e3o existe', val: falhas, bg: 'bg-red-50 border-[rgba(239,68,68,0.2)]', num: 'text-red-500', sub: 'text-red-500', icon: 'M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728', iconCls: 'text-red-500', iconBg: 'bg-[rgba(239,68,68,0.2)]', span: false, onClick: undefined },
                { label: 'Visualizaram', val: read, bg: 'bg-violet-50 border-violet-100', num: 'text-violet-700', sub: 'text-violet-500', icon: 'M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z', iconCls: 'text-violet-600', iconBg: 'bg-violet-100', span: false, onClick: () => openVisualizaram(reportDisparo) },
                { label: 'N\u00e3o entregues', val: naoEntregues, bg: 'bg-yellow-50 border-amber-100', num: 'text-yellow-700', sub: 'text-amber-500', icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', iconCls: 'text-yellow-700', iconBg: 'bg-[rgba(245,158,11,0.2)]', span: true, onClick: () => openNaoEntregues(reportDisparo) },
              ];

              const info = [
                { label: 'RESPOSTAS', val: respostas.toLocaleString(), cls: 'text-emerald-700' },
                { label: 'STATUS', val: STATUS_LABEL[reportDisparo.status]?.label ?? reportDisparo.status, cls: 'text-gray-700' },
                { label: 'REENVIOS', val: reenvios > 0 ? `${reenvios}x` : '\u2014', cls: reenvios > 0 ? 'text-yellow-700' : 'text-gray-400' },
              ];

              return (
                <div className="px-6 py-5 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    {cards.map(c => (
                      <div
                        key={c.label}
                        onClick={c.onClick}
                        className={`flex items-center gap-3 p-3 rounded-xl border ${c.bg}${c.span ? ' col-span-2' : ''}${c.onClick ? ' cursor-pointer hover:brightness-95 transition-all' : ''}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                          <svg className={`w-5 h-5 ${c.iconCls}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={c.icon} />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className={`text-2xl font-bold tabular-nums leading-none ${c.num}`}>{c.val.toLocaleString()}</p>
                          <p className={`text-xs mt-0.5 ${c.sub}`}>{c.label}</p>
                        </div>
                        {c.onClick && (
                          <svg className={`w-4 h-4 ${c.iconCls} opacity-50`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 pt-2 border-t border-gray-100">
                    {info.map(m => (
                      <div key={m.label} className="text-center">
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">{m.label}</p>
                        <p className={`text-sm font-semibold mt-0.5 ${m.cls}`}>{m.val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>,
        document.body
      )}

      {/* Modal Respostas */}
      {respostasDisparo && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh] bg-white border border-gray-200">
            <div className="relative px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-sm font-bold text-gray-900">Respostas â€" {respostasDisparo.disparo.nomeDisparo}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{respostasDisparo.lista.length} resposta{respostasDisparo.lista.length !== 1 ? 's' : ''}</p>
              </div>
              <button
                type="button"
                onClick={() => setRespostasDisparo(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-3">
              {respostasLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">Carregando...</div>
              ) : respostasDisparo.lista.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <svg className="w-8 h-8 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-3 3-3-3z" />
                  </svg>
                  <p className="text-sm">Nenhuma resposta ainda</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {respostasDisparo.lista.map(r => (
                    <div key={r.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs bg-emerald-50 text-emerald-700">
                        {r.phone.slice(-2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-xs font-medium text-gray-700">{r.phone}</span>
                          {r.tipoResposta === 'botao_rapido' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded border border-emerald-200 font-semibold bg-emerald-50 text-emerald-700">BOTÃƒO</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-900 break-words">{r.textoResposta}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(r.receivedAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Visualizaram */}
      {visualizaramDisparo && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh] bg-white border border-gray-200">
            <div className="relative px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-sm font-bold text-gray-900">Visualizaram — {visualizaramDisparo.disparo.nomeDisparo}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{visualizaramDisparo.total} pessoa{visualizaramDisparo.total !== 1 ? 's' : ''} visualizou</p>
              </div>
              <button
                type="button"
                onClick={() => setVisualizaramDisparo(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div
              className="overflow-y-auto flex-1 px-4 py-3"
              onScroll={e => {
                const el = e.currentTarget;
                if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) loadMoreVisualizaram();
              }}
            >
              {visualizaramLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">Carregando...</div>
              ) : visualizaramDisparo.lista.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <svg className="w-8 h-8 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <p className="text-sm">Ninguém visualizou ainda</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {visualizaramDisparo.lista.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-violet-100 bg-violet-50">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 text-violet-700 font-semibold text-xs">
                        {r.phone.slice(-2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700">{r.phone}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          Visualizado em {new Date(r.readAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <svg className="w-4 h-4 text-violet-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </div>
                  ))}
                  {visualizaramLoadingMore && (
                    <div className="flex items-center justify-center py-3 text-gray-400 text-xs">Carregando mais...</div>
                  )}
                  {!visualizaramLoadingMore && visualizaramDisparo.lista.length < visualizaramDisparo.total && (
                    <div className="flex items-center justify-center py-2 text-gray-300 text-xs">
                      {visualizaramDisparo.lista.length} de {visualizaramDisparo.total} — role para carregar mais
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Não Entregues */}
      {naoEntreguesDisparo && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh] bg-white border border-gray-200">
            <div className="relative px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <div>
                <h2 className="text-sm font-bold text-gray-900">Não entregues — {naoEntreguesDisparo.disparo.nomeDisparo}</h2>
                <p className="text-xs text-gray-400 mt-0.5">{naoEntreguesDisparo.lista.length} número{naoEntreguesDisparo.lista.length !== 1 ? 's' : ''} aguardando entrega</p>
              </div>
              <button
                type="button"
                onClick={() => setNaoEntreguesDisparo(null)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 px-4 py-3">
              {naoEntreguesLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-400 text-sm">Carregando...</div>
              ) : naoEntreguesDisparo.lista.length === 0 ? (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 px-1 mb-2 p-2 rounded-lg bg-yellow-50 border border-amber-100">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-yellow-700">
                      Os registros individuais não estão disponíveis para este disparo. As mensagens foram enviadas mas o WhatsApp não retornou confirmação de entrega (2 tiques) — isso ocorre quando a sessão é encerrada antes do retorno do acuse.
                    </p>
                  </div>
                  <div className="flex flex-col items-center justify-center py-6 text-gray-400">
                    <svg className="w-7 h-7 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm">Nenhum número rastreado individualmente</p>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-start gap-2 px-1 mb-2 p-2 rounded-lg bg-yellow-50 border border-amber-100">
                    <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-xs text-yellow-700">
                      Mensagens enviadas mas sem confirmação de entrega do WhatsApp (2 tiques).
                      {naoEntreguesDisparo.lista.length === 0 && (
                        <span> Os registros individuais não estão disponíveis — o celular pode ter recebido após a sessão ser encerrada.</span>
                      )}
                    </p>
                  </div>
                  {naoEntreguesDisparo.lista.map((r, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-amber-100 bg-yellow-50">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs bg-yellow-50 text-yellow-700">
                        {r.phone.slice(-2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-700">{r.phone}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          Enviado em {new Date(r.sentAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0 ${r.status === 'sent' ? 'bg-amber-200 text-yellow-700' : 'bg-gray-200 text-gray-500'}`}>
                        {r.status === 'sent' ? '1 tique' : 'não enviado'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {toast && createPortal(
        <div className="fixed top-0 right-0 z-[99999] flex items-center gap-3 bg-emerald-500 text-white px-5 py-4 shadow-2xl rounded-bl-xl toast-slide-in">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
          <span className="text-sm font-semibold">{toast}</span>
        </div>,
        document.body
      )}

    </div>
  );
}
